import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { X, Plus, Search, Award } from 'lucide-react';

// Sample skills data (in a real app, this would come from an API)
const popularSkills = [
  // Technical Skills
  'JavaScript', 'React', 'Node.js', 'Python', 'Java', 'HTML', 'CSS', 'SQL',
  'TypeScript', 'Angular', 'Vue.js', 'PHP', 'C#', 'C++', 'Ruby', 'Swift',
  'Kotlin', 'Go', 'Rust', 'AWS', 'Azure', 'Google Cloud', 'Docker', 'Kubernetes',
  'MongoDB', 'PostgreSQL', 'MySQL', 'Redis', 'GraphQL', 'REST API', 'Git',
  'CI/CD', 'DevOps', 'Machine Learning', 'Data Science', 'Artificial Intelligence',
  
  // Soft Skills
  'Communication', 'Teamwork', 'Problem Solving', 'Critical Thinking',
  'Time Management', 'Leadership', 'Adaptability', 'Creativity', 'Emotional Intelligence',
  'Negotiation', 'Conflict Resolution', 'Decision Making', 'Project Management',
  
  // Design Skills
  'UI Design', 'UX Design', 'Figma', 'Adobe XD', 'Photoshop', 'Illustrator',
  'Sketch', 'InVision', 'Wireframing', 'Prototyping', 'Visual Design',
  
  // Marketing Skills
  'SEO', 'SEM', 'Content Marketing', 'Social Media Marketing', 'Email Marketing',
  'Google Analytics', 'Growth Hacking', 'Copywriting', 'Brand Management',
  
  // Business Skills
  'Business Analysis', 'Product Management', 'Agile', 'Scrum', 'Kanban',
  'Strategic Planning', 'Data Analysis', 'Market Research', 'Financial Analysis'
];

const SkillsStep = ({ 
  formData, 
  updateFormData, 
  onNext, 
  onPrev, 
  onSkip 
}) => {
  // Initialize skills from formData or from parsed resume data
  const initializeSkills = () => {
    // If we already have skills in formData, use those
    if (formData.skills && formData.skills.length > 0) {
      return typeof formData.skills[0] === 'string' 
        ? formData.skills 
        : formData.skills.map(skill => skill.name);
    }
    
    // Otherwise, check if we have parsed resume data with skills
    if (formData.resume && formData.resume.parsed && formData.resume.parsedData?.skills) {
      const parsedSkills = formData.resume.parsedData.skills;
      // Convert to array of strings if needed
      return parsedSkills.map(skill => typeof skill === 'string' ? skill : skill.name);
    }
    
    // Default to empty array if no skills found
    return [];
  };
  
  // Initialize skill levels from formData
  const initializeSkillLevels = () => {
    const levels = {};
    
    if (formData.skills && formData.skills.length > 0 && typeof formData.skills[0] !== 'string') {
      formData.skills.forEach(skill => {
        if (skill.name && skill.level) {
          levels[skill.name] = skill.level;
        }
      });
    }
    
    return levels;
  };
  
  const [selectedSkills, setSelectedSkills] = useState(initializeSkills());
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [skillLevel, setSkillLevel] = useState(initializeSkillLevels());
  const [showConfetti, setShowConfetti] = useState(false);
  const [parsedFromResume] = useState(formData.resume?.parsed || false);
  const searchInputRef = useRef(null);

  // Filter skills based on search term
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setSearchResults([]);
      return;
    }
    
    const filteredSkills = popularSkills.filter(
      skill => skill.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Filter out already selected skills
    const availableSkills = filteredSkills.filter(
      skill => !selectedSkills.includes(skill)
    );

    setSearchResults(availableSkills.slice(0, 10));
  }, [searchTerm, selectedSkills]);

  // Handle skill selection
  const handleSelectSkill = (skill) => {
    if (!selectedSkills.includes(skill)) {
      const newSelectedSkills = [...selectedSkills, skill];
      setSelectedSkills(newSelectedSkills);
      
      // Clear search term and results
      setSearchTerm('');
      setSearchResults([]);
      
      // Focus back on search input
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
      
      // Show confetti if 5 skills are selected
      if (newSelectedSkills.length === 5 && !showConfetti) {
        setShowConfetti(true);
        setTimeout(() => setShowConfetti(false), 3000);
      }
    }
  };

  // Handle skill removal
  const handleRemoveSkill = (skillToRemove) => {
    setSelectedSkills(selectedSkills.filter(skill => skill !== skillToRemove));
    
    // Also remove the skill level
    const newSkillLevel = { ...skillLevel };
    delete newSkillLevel[skillToRemove];
    setSkillLevel(newSkillLevel);
  };

  // Handle skill level change
  const handleSkillLevelChange = (skill, level) => {
    setSkillLevel({
      ...skillLevel,
      [skill]: level
    });
  };

  // Get recommended skills based on already selected skills and resume data
  const getRecommendedSkills = () => {
    // This is a recommendation algorithm that uses both selected skills and resume data
    const recommendations = new Set();
    
    // Map of related skills
    const relatedSkillsMap = {
      'JavaScript': ['React', 'Angular', 'Vue.js', 'Node.js', 'TypeScript', 'Redux', 'Webpack'],
      'React': ['JavaScript', 'Redux', 'TypeScript', 'HTML', 'CSS', 'React Native', 'Next.js'],
      'Node.js': ['JavaScript', 'Express', 'MongoDB', 'REST API', 'GraphQL', 'Microservices'],
      'Python': ['Django', 'Flask', 'Machine Learning', 'Data Science', 'NumPy', 'Pandas', 'TensorFlow'],
      'Java': ['Spring', 'Hibernate', 'Android', 'Microservices', 'JUnit', 'Maven', 'Gradle'],
      'HTML': ['CSS', 'JavaScript', 'Responsive Design', 'Web Development', 'SASS', 'Bootstrap'],
      'CSS': ['HTML', 'SASS', 'LESS', 'Tailwind CSS', 'Bootstrap', 'Styled Components'],
      'SQL': ['MySQL', 'PostgreSQL', 'Database Design', 'Data Analysis', 'NoSQL', 'MongoDB'],
      'UI Design': ['UX Design', 'Figma', 'Adobe XD', 'Sketch', 'Prototyping', 'Wireframing'],
      'UX Design': ['UI Design', 'User Research', 'Usability Testing', 'Information Architecture', 'Figma'],
      'Project Management': ['Agile', 'Scrum', 'Kanban', 'JIRA', 'Confluence', 'Product Management'],
      'Data Analysis': ['SQL', 'Excel', 'Tableau', 'Power BI', 'Python', 'R', 'Statistics'],
      'Machine Learning': ['Python', 'TensorFlow', 'PyTorch', 'Scikit-learn', 'Data Science', 'AI'],
      'DevOps': ['Docker', 'Kubernetes', 'CI/CD', 'AWS', 'Azure', 'Jenkins', 'Terraform'],
      'Cloud': ['AWS', 'Azure', 'Google Cloud', 'Serverless', 'Docker', 'Kubernetes'],
      'Mobile Development': ['React Native', 'Flutter', 'Swift', 'Kotlin', 'Android', 'iOS'],
      'Digital Marketing': ['SEO', 'SEM', 'Content Marketing', 'Social Media', 'Google Analytics'],
      'Graphic Design': ['Photoshop', 'Illustrator', 'InDesign', 'Typography', 'Color Theory']};
    
    // Check if we have resume data with parsed skills
    const resumeSkills = [];
    if (formData.resume && formData.resume.parsed && formData.resume.parsedData?.skills) {
      // Get skills from resume that aren't already selected
      formData.resume.parsedData.skills.forEach(skill => {
        const skillName = typeof skill === 'string' ? skill : skill.name;
        if (!selectedSkills.includes(skillName) && !resumeSkills.includes(skillName)) {
          resumeSkills.push(skillName);
        }
      });
    }
    
    // First add remaining skills from resume that aren't already selected
    resumeSkills.forEach(skill => {
      if (!selectedSkills.includes(skill)) {
        recommendations.add(skill);
      }
    });
    
    // Then add related skills based on already selected skills
    selectedSkills.forEach(skill => {
      if (relatedSkillsMap[skill]) {
        relatedSkillsMap[skill].forEach(relatedSkill => {
          if (!selectedSkills.includes(relatedSkill)) {
            recommendations.add(relatedSkill);
          }
        });
      }
    });
    
    // If we have resume experience data, extract skills from job titles and descriptions
    if (formData.resume && formData.resume.parsed && formData.resume.parsedData?.experience) {
      const experience = formData.resume.parsedData.experience;
      
      // Common keywords that might indicate skills
      const skillKeywords = [
        'JavaScript', 'React', 'Angular', 'Vue', 'Node', 'Python', 'Java', 'HTML', 'CSS',
        'SQL', 'TypeScript', 'PHP', 'C#', 'C++', 'Ruby', 'Swift', 'Kotlin', 'Go', 'Rust',
        'AWS', 'Azure', 'Google Cloud', 'Docker', 'Kubernetes', 'MongoDB', 'PostgreSQL', 'MySQL',
        'Redis', 'GraphQL', 'REST', 'Git', 'DevOps', 'Machine Learning', 'Data Science', 'AI',
        'Agile', 'Scrum', 'Kanban', 'JIRA', 'UI', 'UX', 'Figma', 'Adobe XD', 'Photoshop',
        'Illustrator', 'SEO', 'SEM', 'Content Marketing', 'Social Media', 'Analytics'
      ];
      
      // Extract potential skills from job titles and descriptions
      experience.forEach(job => {
        // Check job title for skills
        skillKeywords.forEach(keyword => {
          if (job.title && job.title.includes(keyword) && !selectedSkills.includes(keyword)) {
            recommendations.add(keyword);
          }
          
          // Check job description for skills
          if (job.description && job.description.includes(keyword) && !selectedSkills.includes(keyword)) {
            recommendations.add(keyword);
          }
        });
      });
    }
    
    // If we have education data, extract skills from fields of study
    if (formData.resume && formData.resume.parsed && formData.resume.parsedData?.education) {
      const education = formData.resume.parsedData.education;
      
      // Map of fields to related skills
      const fieldSkillsMap = {
        'Computer Science': ['Algorithms', 'Data Structures', 'Software Development', 'Programming'],
        'Information Technology': ['Networking', 'System Administration', 'IT Support', 'Cybersecurity'],
        'Data Science': ['Statistics', 'Data Analysis', 'Machine Learning', 'Big Data'],
        'Design': ['UI Design', 'UX Design', 'Graphic Design', 'Visual Communication'],
        'Business': ['Business Analysis', 'Project Management', 'Marketing', 'Finance'],
        'Marketing': ['Digital Marketing', 'Brand Management', 'Market Research', 'Social Media'],
        'Engineering': ['Problem Solving', 'Technical Documentation', 'Quality Assurance']
      };
      
      education.forEach(edu => {
        if (edu.field) {
          // Check if any of our field keys are in the education field
          Object.keys(fieldSkillsMap).forEach(field => {
            if (edu.field.includes(field)) {
              // Add related skills
              fieldSkillsMap[field].forEach(skill => {
                if (!selectedSkills.includes(skill)) {
                  recommendations.add(skill);
                }
              });
            }
          });
        }
      });
    }
    
    return Array.from(recommendations).slice(0, 8);
  };

  // Handle continue button click
  const handleContinue = () => {
    // Create an array of skill objects with name and level
    const skillsWithLevels = selectedSkills.map(skill => ({
      name: skill,
      level: skillLevel[skill] || 'intermediate' // Default to intermediate if not set
    }));
    
    updateFormData('skills', skillsWithLevels);
    onNext();
  };

  // Get skill level label
  const getSkillLevelLabel = (level) => {
    switch (level) {
      case 'beginner':
        return 'Beginner';
      case 'intermediate':
        return 'Intermediate';
      case 'advanced':
        return 'Advanced';
      case 'expert':
        return 'Expert';
      default:
        return 'Intermediate';
    }
  };

  // Get skill level color
  const getSkillLevelColor = (level) => {
    switch (level) {
      case 'beginner':
        return 'bg-blue-100 text-blue-800';
      case 'intermediate':
        return 'bg-green-100 text-green-800';
      case 'advanced':
        return 'bg-purple-100 text-purple-800';
      case 'expert':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-green-100 text-green-800';
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <h1 className="text-3xl font-bold text-center mb-2">Showcase Your Skills 🛠️</h1>
        <p className="text-gray-600 text-center mb-8">
          Add skills that highlight your strengths and make you stand out to employers
        </p>

        {/* Search and add skills */}
        <div className="mb-8">
          <label htmlFor="skill-search" className="block text-sm font-medium text-gray-700 mb-1">
            Search and add skills
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search size={18} className="text-gray-400" />
            </div>
            <input
              type="text"
              id="skill-search"
              ref={searchInputRef}
              className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
              placeholder="Type to search skills (e.g., JavaScript, Project Management)"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onFocus={() => setShowResults(true)}
              onBlur={() => setTimeout(() => setShowResults(false), 200)}
            />
            
            {/* Search results */}
            {showResults && searchResults.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-y-auto">
                {searchResults.map((skill, index) => (
                  <div
                    key={index}
                    className="px-4 py-2 hover:bg-gray-100 cursor-pointer flex items-center justify-between"
                    onMouseDown={() => handleSelectSkill(skill)}
                  >
                    <span>{skill}</span>
                    <Plus size={16} className="text-gray-500" />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Notification for parsed resume data */}
        {parsedFromResume && selectedSkills.length > 0 && (
          <div className="bg-blue-50 border border-blue-100 rounded-md p-4 mb-6">
            <div className="flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-500 mr-2" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <p className="text-sm text-blue-700">
                We've identified skills from your resume. Feel free to add more or adjust your proficiency levels.
              </p>
            </div>
          </div>
        )}
        
        {/* Selected skills */}
        <div className="mb-8">
          <h3 className="text-lg font-medium text-gray-700 mb-4 flex items-center">
            Your Skills 
            <span className="ml-2 text-sm text-gray-500">
              ({selectedSkills.length} selected)
            </span>
            {selectedSkills.length >= 5 && (
              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                <Award size={14} className="mr-1" />
                Great selection!
              </span>
            )}
            {parsedFromResume && selectedSkills.length > 0 && (
              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
                  <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd" />
                </svg>
                From Resume
              </span>
            )}
          </h3>
          
          {selectedSkills.length === 0 ? (
            <div className="p-6 border border-dashed border-gray-300 rounded-md text-center">
              <p className="text-gray-500">
                No skills selected yet. Search and add skills above.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {selectedSkills.map((skill, index) => (
                <motion.div
                  key={skill}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                  className="p-4 border border-gray-200 rounded-md bg-white shadow-sm"
                >
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-medium text-gray-800">{skill}</h4>
                    <button
                      type="button"
                      className="text-gray-400 hover:text-red-500 transition-colors"
                      onClick={() => handleRemoveSkill(skill)}
                    >
                      <X size={18} />
                    </button>
                  </div>
                  
                  {/* Skill level selector */}
                  <div>
                    <p className="text-sm text-gray-600 mb-2">How would you rate your proficiency?</p>
                    <div className="flex space-x-2">
                      {['beginner', 'intermediate', 'advanced', 'expert'].map((level) => (
                        <button
                          key={level}
                          type="button"
                          className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                            skillLevel[skill] === level 
                              ? getSkillLevelColor(level) 
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                          onClick={() => handleSkillLevelChange(skill, level)}
                        >
                          {getSkillLevelLabel(level)}
                        </button>
                      ))}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>

        {/* Recommended skills */}
        {selectedSkills.length > 0 && (
          <div className="mb-8">
            <h3 className="text-lg font-medium text-gray-700 mb-4 flex items-center">
              Recommended Skills for You
              {parsedFromResume && (
                <span className="ml-2 text-sm text-blue-600 flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  Based on your resume
                </span>
              )}
            </h3>
            <div className="flex flex-wrap gap-2">
              {getRecommendedSkills().length > 0 ? (
                getRecommendedSkills().map((skill, index) => (
                  <motion.button
                    key={index}
                    type="button"
                    className="px-4 py-2 bg-blue-50 text-blue-700 rounded-md hover:bg-blue-100 transition-colors flex items-center"
                    onClick={() => handleSelectSkill(skill)}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                  >
                    <Plus size={16} className="mr-1" />
                    {skill}
                  </motion.button>
                ))
              ) : (
                <p className="text-gray-500 italic">No additional recommendations based on your current selections.</p>
              )}
            </div>
          </div>
        )}

        {/* Skill tips */}
        <div className="p-4 bg-yellow-50 border border-yellow-100 rounded-md mb-8">
          <h3 className="text-md font-medium text-yellow-800 mb-2 flex items-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            Pro Tips
          </h3>
          <ul className="text-sm text-yellow-700 space-y-1 ml-6 list-disc">
            <li>Add at least 5-10 skills for better job matches</li>
            <li>Include both technical and soft skills</li>
            <li>Be honest about your proficiency levels</li>
            <li>Add skills that are relevant to your target jobs</li>
          </ul>
        </div>

        {/* Navigation buttons */}
        <div className="flex justify-between mt-8">
          <button
            type="button"
            className="px-6 py-2 border border-gray-300 text-gray-700 font-medium rounded-md hover:bg-gray-50 transition-colors"
            onClick={onPrev}
          >
            Back
          </button>
          
          <div className="flex gap-4">
            <button
              type="button"
              className="px-6 py-2 text-gray-500 font-medium hover:text-gray-700 transition-colors"
              onClick={onSkip}
            >
              Skip for now
            </button>
            
            <motion.button
              type="button"
              className={`px-8 py-3 bg-black text-white font-bold rounded-md shadow-md hover:bg-gray-800 transition-all ${
                selectedSkills.length >= 5 ? 'bg-green-600 hover:bg-green-700' : ''
              }`}
              onClick={handleContinue}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Continue
            </motion.button>
          </div>
        </div>
        
        {/* Confetti effect */}
        {showConfetti && (
          <div className="fixed inset-0 pointer-events-none z-50">
            {/* This would be replaced with the actual ConfettiEffect component */}
            <div className="absolute top-0 left-0 w-full h-full overflow-hidden">
              {/* Confetti animation would go here */}
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default SkillsStep;
